import { createActivityStore, createSessionStore } from "@/app/activity/store"
import { useTranslation } from "react-i18next"
import Dialog from "@/components/dialog/Dialog"
import { Session, UpsertSessionPayload } from "@/types/session"
import { useEffect, useState } from "react"
import { cloneDeep, isEqual } from "lodash"
import { useUpsertSessionMutation } from "@/hooks/api/useSessionQuery"
import { createGlobalStore } from "@/stores/global"
import { isBefore } from "date-fns"

export default function ConfirmSaveSessionDialog({ isCropping }: { isCropping: boolean }) {
  const { t } = useTranslation()
  const { mutateAsync: updateSession } = useUpsertSessionMutation()
  const { session, setSession, confirmSaveDialogOpen, setConfirmSaveDialogOpen } =
    createSessionStore((state) => ({
      session: state.temporarySession,
      setTemporarySession: state.setTemporarySession,
      setSession: state.setSession,
      confirmSaveDialogOpen: state.confirmSaveDialogOpen,
      setConfirmSaveDialogOpen: state.setConfirmSaveDialogOpen,
    }))
  const { setSnackbar } = createGlobalStore((state) => ({
    setSnackbar: state.setSnackBar,
  }))

  const { activity } = createActivityStore((state) => ({
    activity: state.activity,
  }))

  const [isOpen, setIsOpen] = useState(false)
  const [copiedSession, setCopiedSession] = useState<Session | undefined>()

  const closeDialog = () => {
    setIsOpen(false)
    setConfirmSaveDialogOpen(false)

    if (isOpen || isEqual(copiedSession, session)) {
      setSession(undefined)
    }
  }

  const confirmSave = async () => {
    if (session) {
      if (
        isBefore(new Date(activity?.dateFinish as string), new Date(session.dateFinish)) ||
        isBefore(new Date(session.dateStart), new Date(activity?.dateStart as string))
      ) {
        setSnackbar({
          message: t("messages.notAppropriateSessionTimeDuration"),
          severity: "warning",
          open: true,
        })

        setIsOpen(false)
        setConfirmSaveDialogOpen(false)

        return
      }
      try {
        await updateSession({
          idfSession: session.idfSession,
          payload: session as unknown as UpsertSessionPayload,
        })
        setSnackbar({ severity: "success", message: t("messages.successUpdate"), open: true })

        setIsOpen(false)
        setConfirmSaveDialogOpen(false)
        setSession(undefined)
      } catch (e) {
        console.error(e)
      }
    }
  }

  useEffect(() => {
    if (session && copiedSession?.idfSession !== session?.idfSession) {
      setCopiedSession(cloneDeep(session))
    }

    if (!session) {
      setCopiedSession(undefined)
    }
  }, [session])

  useEffect(() => {
    if (isCropping) {
      setIsOpen(false)
      setConfirmSaveDialogOpen(false)

      return
    }

    if (confirmSaveDialogOpen && !isEqual(copiedSession, session)) {
      setIsOpen(true)
    }
  }, [confirmSaveDialogOpen, copiedSession, session, isCropping])

  return (
    <Dialog
      title={t("confirm")}
      open={isOpen}
      onClose={() => {
        if (!isCropping) {
          closeDialog()
        }
      }}
      onConfirm={confirmSave}
      maxWidth="400px">
      {t("messages.sessionTimeChanged")}
    </Dialog>
  )
}
